import React, { useEffect, useRef, useState } from 'react'

interface PannellumViewerProps {
  url?: string;
  videoUrl?: string;
  isVideo?: boolean;
  videoType?: 'equirectangular' | 'cubemap';
  onError?: (error: any) => void;
  onVideoCreate?: (video: HTMLVideoElement) => void;
}

const PannellumViewer: React.FC<PannellumViewerProps> = ({
  url,
  videoUrl,
  isVideo = false,
  videoType = 'equirectangular',
  onError,
  onVideoCreate
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<any>(null)
  
  const finalVideoUrl = videoUrl || url || '';

  useEffect(() => {
    if (!containerRef.current) return

    try {
      if (!isVideo && url) {
        const viewer = (window as any).pannellum.viewer(containerRef.current, {
          type: 'equirectangular',
          panorama: url,
          autoLoad: true,
          showControls: true
        });
        viewerRef.current = viewer;
        return () => { viewer.destroy(); };
      }

      if (!finalVideoUrl) return;

      const videoElement = document.createElement('video')
      videoElement.src = finalVideoUrl
      videoElement.crossOrigin = 'anonymous'
      videoElement.autoplay = true
      videoElement.loop = true
      videoElement.muted = true
      videoElement.playsInline = true
      videoElement.style.display = 'none' 
      videoElement.style.position = 'absolute'
      videoElement.style.width = '1px'
      videoElement.style.height = '1px'
      
      document.body.appendChild(videoElement)

      videoElement.onloadedmetadata = () => {
        videoElement.width = videoElement.videoWidth || 1920;
        videoElement.height = videoElement.videoHeight || 1080;

        if (onVideoCreate) {
          onVideoCreate(videoElement);
        }

        const viewer = (window as any).pannellum.viewer(containerRef.current, {
          type: videoType,
          panorama: videoElement,  
          autoLoad: true,
          showControls: true,
          hfov: 100,
          pitch: 0,
          yaw: 0,
          minHfov: 30,
          maxHfov: 150,
          mouseZoom: true,
          doubleClickZoom: true,
          keyboardZoom: true,
          friction: 0.05,
          showFullscreenCtrl: true,
          dynamic: true,
          dynamicUpdate: true
        })

        viewerRef.current = viewer
      };

      videoElement.addEventListener('error', (e) => {
        console.error('Video load error', e)
        if (onError) onError(e)
      })

      return () => {
        if (viewerRef.current) {
          viewerRef.current.destroy()
        }
        videoElement.pause()
        videoElement.src = ''
        if (document.body.contains(videoElement)) {
          document.body.removeChild(videoElement)
        }
      }
    } catch (error) {
      console.error('Pannellum initialization error:', error)
      if (onError) onError(error)
    }
  }, [finalVideoUrl, videoType, isVideo, url, onError, onVideoCreate])

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#000'
      }}
    />
  )
}

export default PannellumViewer
